/**
 * Gmail-style category tabs: each tab has an icon, a label, an optional pill ("3 new") and, under it, a one-line
 * preview ("InfoMoney — Resgate já…", core mailTabPreview) while it isn't selected, a dash when there's nothing to
 * preview, so no tab is shorter than another. Every tab is the same fixed width (size.categoryTab.width) whatever its
 * text, so tabs don't resize as previews come and go; a long preview ends in an ellipsis. When they don't fit the bar's width (a thread open beside the list, a phone) the bar scrolls sideways (trackpad, shift+wheel,
 * touch swipe) with a hidden scrollbar, the selected tab is scrolled into view, and the edge with more tabs fades out.
 * iOS/Android: the same cells in a horizontal ScrollView / horizontalScroll row.
 */
import type { IconName } from "@bundle/tokens";
import { useLayoutEffect, useRef, useState } from "react";
import { Chip, type ChipTone } from "./Chip.tsx";
import { Icon } from "./Icon.tsx";
import { Text } from "./Text.tsx";
import { MAIL_TAB_NO_PREVIEW } from "@bundle/core/logic/mail";
import { cx } from "./tokens.ts";
import "./mailcal.css";

export type CategoryTab<K extends string = string> = {
  value: K;
  label: string;
  icon: IconName;
  /** "3 new", in the pill's tone. `count` is the number in it. */
  pill?: { label: string; tone: ChipTone; count?: number };
  /** The second line; without one the tab shows a dash (MAIL_TAB_NO_PREVIEW). */
  preview?: string;
  emphasized?: boolean;
};

export type CategoryTabsProps<K extends string = string> = {
  tabs: CategoryTab<K>[];
  value: K;
  onChange: (value: K) => void;
  accessibilityLabel: string;
  testID: string;
};

/** Which edges have tabs scrolled out of sight (each gets a fade). */
type More = { start: boolean; end: boolean };

/** Which edges have tabs scrolled out of sight. RTL scrolls to a negative scrollLeft: the start distance is its size. */
function measureMore(el: HTMLElement, set: (f: (m: More) => More) => void): void {
  const from = Math.abs(el.scrollLeft);
  const next = { start: from > 1, end: el.scrollWidth - el.clientWidth - from > 1 };
  set((m) => (m.start === next.start && m.end === next.end ? m : next));
}

/** Scrolls the selected tab (`[aria-selected]`) into view when it's out of sight; by the boxes on screen, so RTL too. */
function reveal(el: HTMLElement, smooth: boolean): void {
  const tab = el.querySelector<HTMLElement>('[aria-selected="true"]');
  if (tab === null) return;
  const b = el.getBoundingClientRect();
  const r = tab.getBoundingClientRect();
  const by = r.left < b.left ? r.left - b.left : r.right > b.right ? r.right - b.right : 0;
  if (by === 0) return;
  const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof el.scrollBy === "function") el.scrollBy({ left: by, behavior: smooth && !reduce ? "smooth" : "auto" });
  else el.scrollLeft += by;
}

export function CategoryTabs<K extends string = string>({ tabs, value, onChange, accessibilityLabel, testID }: CategoryTabsProps<K>) {
  const bar = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState<More>({ start: false, end: false });
  // Whether the selected tab is kept in view as the tabs lay out (their text and styles arriving, a pill appearing):
  // from each new selection until the person scrolls the bar themselves.
  const follow = useRef(true);
  // Re-measured when the bar or any tab changes size and when tabs come and go; the selected tab is brought back into
  // view then too while followed, and whenever the bar itself changes width (a divider drag, a window resize).
  useLayoutEffect(() => {
    const el = bar.current;
    if (el === null) return undefined;
    const update = () => measureMore(el, setMore);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== width || follow.current) reveal(el, false);
      width = el.clientWidth;
      update();
    });
    const observe = () => {
      ro.disconnect();
      ro.observe(el);
      for (const tab of Array.from(el.children)) ro.observe(tab);
    };
    observe();
    const mo = new MutationObserver(() => {
      observe();
      if (follow.current) reveal(el, false);
      update();
    });
    mo.observe(el, { childList: true });
    const own = () => {
      follow.current = false;
    };
    for (const type of ["wheel", "touchstart", "keydown"] as const) el.addEventListener(type, own, { passive: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
      for (const type of ["wheel", "touchstart", "keydown"] as const) el.removeEventListener(type, own);
    };
  }, []);
  // A new selection: scrolled into view (on mount at once, after that smoothly).
  const shown = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    const el = bar.current;
    if (el === null) return;
    follow.current = true;
    reveal(el, shown.current !== undefined && shown.current !== value);
    shown.current = value;
    measureMore(el, setMore);
  }, [value]);
  return (
    <div className={cx("b-cattabs", more.start && "b-cattabs-more-start", more.end && "b-cattabs-more-end")}>
      <div ref={bar} className="b-cattabs-scroll" role="tablist" aria-label={accessibilityLabel} data-testid={testID} onScroll={(e) => measureMore(e.currentTarget, setMore)}>
        {tabs.map((tab) => {
          const selected = tab.value === value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={selected}
              className={cx("b-cattab", selected && "is-selected")}
              onClick={() => onChange(tab.value)}
              data-testid={`${testID}-${tab.value}`}
            >
              <span className="b-cattab-head">
                <Icon name={tab.icon} size="icon.sm" color={selected ? "accent.text" : "text.secondary"} />
                <span className="b-cattab-label">
                  <Text variant="footnote" weight={selected || tab.emphasized === true ? "semibold" : "regular"} color={selected ? "accent.text" : "text.primary"} lines={1}>
                    {tab.label}
                  </Text>
                </span>
                {tab.pill === undefined ? null : <Chip label={tab.pill.label} tone={tab.pill.tone} />}
              </span>
              {selected ? null : (
                <span className="b-cattab-preview">
                  <Text variant="caption" color="text.secondary" lines={1}>
                    {tab.preview === undefined || tab.preview.trim() === "" ? MAIL_TAB_NO_PREVIEW : tab.preview}
                  </Text>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
